import type React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { type UnitOfMeasure, formatPointsForDisplay } from '@/lib/units/unit-conversion'
import {
  buildSpacingOptions,
  findSpacingPresetLabel,
  normalizeSpacingPoints,
  resolveSpacingInput,
} from '../helpers/spacing-value'

type UseSpacingValueArgs = {
  unitOfMeasure: UnitOfMeasure
  spacingUnitSuffix: string
  autoDistribute: boolean
  canSpaceSelection: boolean
}

/** Manual spacing amount: presets, slider and typed input, all in points. */
export function useSpacingValue({
  unitOfMeasure,
  spacingUnitSuffix,
  autoDistribute,
  canSpaceSelection,
}: UseSpacingValueArgs) {
  const [spaceBetween, setSpaceBetween] = useState([0])
  const [spacingInputValue, setSpacingInputValue] = useState('0')
  const [selectedSpacing, setSelectedSpacing] = useState<string | null>(null)

  const spacingOptions = useMemo(
    () => buildSpacingOptions(unitOfMeasure, spacingUnitSuffix),
    [spacingUnitSuffix, unitOfMeasure]
  )

  const handleSpacingValueChange = useCallback(
    (
      value: number[],
      options?: { preview?: boolean; updateInput?: boolean; decimalPlaces?: number }
    ) => {
      const nextValue = normalizeSpacingPoints(value[0] ?? 0)

      setSpaceBetween([nextValue])

      if (options?.updateInput !== false) {
        setSpacingInputValue(
          formatPointsForDisplay(
            nextValue,
            unitOfMeasure,
            options?.decimalPlaces !== undefined
              ? { maxDecimals: 4, decimalPlaces: options.decimalPlaces }
              : { maxDecimals: 4 }
          )
        )
      }

      setSelectedSpacing(findSpacingPresetLabel(spacingOptions, nextValue))

      if (autoDistribute || !canSpaceSelection) {
        return
      }
    },
    [autoDistribute, canSpaceSelection, spacingOptions, unitOfMeasure]
  )

  const commitSpacingInputValue = useCallback(() => {
    const { points, decimalPlaces } = resolveSpacingInput(
      spacingInputValue.trim(),
      unitOfMeasure,
      spacingOptions
    )

    handleSpacingValueChange([points], { decimalPlaces })
  }, [handleSpacingValueChange, spacingInputValue, spacingOptions, unitOfMeasure])

  const handleSpacingInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.currentTarget.blur()
        commitSpacingInputValue()
        return
      }

      if (
        event.key === 'Backspace' ||
        event.key === 'Delete' ||
        event.key === 'Tab' ||
        event.key.startsWith('Arrow')
      ) {
        return
      }

      if (event.ctrlKey || event.metaKey) {
        return
      }
    },
    [commitSpacingInputValue]
  )

  useEffect(() => {
    setSpacingInputValue(formatPointsForDisplay(spaceBetween[0], unitOfMeasure, { maxDecimals: 4 }))
    setSelectedSpacing(findSpacingPresetLabel(spacingOptions, spaceBetween[0]))
  }, [spaceBetween, spacingOptions, unitOfMeasure])

  return {
    spaceBetween,
    spacingInputValue,
    setSpacingInputValue,
    selectedSpacing,
    spacingOptions,
    handleSpacingValueChange,
    commitSpacingInputValue,
    handleSpacingInputKeyDown,
  }
}
